<template>
  <div class="group flex items-center gap-2">
    <!-- Hidden Native Radio Input -->
    <input
      type="radio"
      class="peer hidden"
      :id="id"
      :name="name"
      :value="value"
      :checked="modelValue === value"
      @change="$emit('update:modelValue', value)"
      :disabled="disabled"
    />

    <!-- Custom Radio Button -->
    <div
      class="flex cursor-pointer items-center justify-center rounded-full border border-solid focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-turquoise-40 group-hover:border-turquoise-40 group-hover:bg-turquoise-10 border-gray"
      :class="[
        {
          'border-turquoise-40 bg-turquoise-10': modelValue === value,
          'active:border-turquoise-40 active:bg-turquoise-10': !disabled,
        },
        sizeClasses.radio,
      ]"
      @click="!disabled && $emit('update:modelValue', value)"
      tabindex="0"
      @keyup.enter="!disabled && $emit('update:modelValue', value)"
      :aria-checked="modelValue === value"
      role="radio"
      :aria-labelledby="`label-${id}`"
    >
      <!-- Custom Icon (if provided) -->
      <template v-if="customIcon">
        <i :class="[customIcon, sizeClasses.icon, { 'text-white': modelValue === value }]"></i>
      </template>
      <!-- Default inner dot when checked -->
      <template v-else>
        <div
          class="rounded-full transition-all duration-200"
          :class="[
            sizeClasses.dot,
            {
              'scale-100 bg-turquoise-60': modelValue === value,
              'scale-0 bg-transparent': modelValue !== value,
            },
          ]"
        ></div>
      </template>
    </div>

    <!-- Label -->
    <label
      v-if="label"
      :for="id"
      :id="`label-${id}`"
      class="cursor-pointer transition-all"
      :class="[sizeClasses.label, disabled ? 'text-gray-70' : 'text-gray-700']"
      tabindex="0"
      @keyup.enter="$emit('update:modelValue', value)"
    >
      {{ label }}
    </label>
  </div>
</template>

<script>
export default {
  name: 'RadioButton',
  props: {
    modelValue: [String, Number, Boolean],
    id: { type: String, required: true },
    name: { type: String, required: true }, // Radios in the same group should have the same name
    value: { type: [String, Number, Boolean], required: true },
    label: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    customIcon: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  computed: {
    sizeClasses() {
      const sizes = {
        sm: { radio: 'w-4 h-4', dot: 'w-[4px] h-[4px]', label: 'text-sm', icon: 'text-xs' },
        md: {
          radio: 'w-[18px] h-[18px]',
          dot: 'w-[6px] h-[6px]',
          label: 'text-base',
          icon: 'text-sm',
        },
        lg: { radio: 'w-6 h-6', dot: 'w-3 h-3', label: 'text-lg', icon: 'text-base' },
      };
      return sizes[this.size] || sizes.md;
    },
  },
};
</script>
